import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  api,
  unwrap,
  type DisposalSummary,
  type RetentionFile,
} from "../api/client";
import { utc } from "../import/items";

const STATE: Record<
  string,
  { label: string; color: "default" | "info" | "success" | "error" }
> = {
  requested: { label: "Awaiting approval", color: "info" },
  rejected: { label: "Rejected", color: "default" },
  disposing: { label: "Disposing…", color: "info" },
  done: { label: "Disposed of", color: "success" },
  failed: { label: "Failed", color: "error" },
};

const plural = (n: number, what: string) => `${n} ${what}${n === 1 ? "" : "s"}`;

const day = (t: string) => utc(t).slice(0, 10);

/**
 * Retention and disposal (threat model §2.2): what falls due, legal holds,
 * and disposals, which someone else approves before anything is removed.
 */
export function RetentionPage() {
  return (
    <Stack spacing={3} component="section" aria-label="Retention">
      <Typography variant="h5" component="h2">
        Retention
      </Typography>
      <Typography color="text.secondary">
        Recordings are kept for their cases&apos; retention class, or for a set
        time when they are in no case. When they fall due they are listed here
        for review; nothing is deleted until someone asks for its disposal and
        another person approves it. A legal hold keeps recordings whatever their
        dates.
      </Typography>
      <Review />
      <Disposals />
      <Holds />
    </Stack>
  );
}

function Review() {
  const qc = useQueryClient();
  const [all, setAll] = useState(false);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [action, setAction] = useState<"dispose" | "keep" | "hold" | null>(
    null,
  );
  const review = useQuery({
    queryKey: ["retention", "review", all],
    queryFn: () =>
      unwrap(api.GET("/api/retention/review", { params: { query: { all } } })),
  });
  const files = review.data?.files ?? [];
  const picked = files.filter((f) => chosen.has(f.id));
  const toggle = (id: string) =>
    setChosen((c) => {
      const n = new Set(c);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const done = () => {
    setAction(null);
    setChosen(new Set());
    void qc.invalidateQueries({ queryKey: ["retention"] });
    void qc.invalidateQueries({ queryKey: ["disposals"] });
    void qc.invalidateQueries({ queryKey: ["holds"] });
  };

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      component="section"
      aria-label="Review"
    >
      <Stack
        direction="row"
        sx={{ alignItems: "center", gap: 2, flexWrap: "wrap", mb: 1 }}
      >
        <Typography variant="h6" component="h3" sx={{ flexGrow: 1 }}>
          Due for review
        </Typography>
        <FormControlLabel
          control={
            <Switch checked={all} onChange={(e) => setAll(e.target.checked)} />
          }
          label="Show every recording"
        />
      </Stack>
      {review.data && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Recordings in no case are kept{" "}
          {plural(review.data.settings.unassigned_days, "day")} from when they
          were made. This list looks{" "}
          {plural(review.data.settings.warning_days, "day")} ahead.
        </Typography>
      )}
      {review.isError && <Alert severity="error">{review.error.message}</Alert>}
      {review.data && files.length === 0 ? (
        <Typography color="text.secondary">Nothing is due.</Typography>
      ) : (
        <TableContainer sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader aria-label="Recordings due">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={files.length > 0 && picked.length === files.length}
                    indeterminate={
                      picked.length > 0 && picked.length < files.length
                    }
                    onChange={(e) =>
                      setChosen(
                        new Set(e.target.checked ? files.map((f) => f.id) : []),
                      )
                    }
                    slotProps={{ input: { "aria-label": "Choose all" } }}
                  />
                </TableCell>
                <TableCell>Recording</TableCell>
                <TableCell>Recorded (UTC)</TableCell>
                <TableCell>Cases</TableCell>
                <TableCell>Due</TableCell>
                <TableCell>Why</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {files.map((f) => (
                <FileRow
                  key={f.id}
                  f={f}
                  checked={chosen.has(f.id)}
                  onToggle={() => toggle(f.id)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {picked.length > 0 && (
        <Stack direction="row" sx={{ gap: 1, mt: 2, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            color="error"
            disabled={!picked.every((f) => f.can_dispose)}
            onClick={() => setAction("dispose")}
          >
            Request disposal of {picked.length}
          </Button>
          {!picked.every((f) => f.can_dispose) && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ alignSelf: "center" }}
            >
              {picked.filter((f) => !f.can_dispose).length} of these can&apos;t
              be disposed of (on hold, already requested, or not yours to
              dispose of).
            </Typography>
          )}
          <Button variant="outlined" onClick={() => setAction("keep")}>
            Keep longer
          </Button>
          <Button
            variant="outlined"
            disabled={picked.length !== 1}
            onClick={() => setAction("hold")}
          >
            Place legal hold
          </Button>
        </Stack>
      )}
      {action === "dispose" && (
        <RequestDisposal
          files={picked}
          onClose={() => setAction(null)}
          onDone={done}
        />
      )}
      {action === "keep" && (
        <KeepLonger
          files={picked}
          onClose={() => setAction(null)}
          onDone={done}
        />
      )}
      {action === "hold" && picked[0] && (
        <PlaceHold
          kind="file"
          id={picked[0].id}
          name={picked[0].original_name}
          onClose={() => setAction(null)}
          onDone={done}
        />
      )}
    </Paper>
  );
}

function FileRow({
  f,
  checked,
  onToggle,
}: {
  f: RetentionFile;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <TableRow hover selected={checked}>
      <TableCell padding="checkbox">
        <Checkbox
          checked={checked}
          onChange={onToggle}
          slotProps={{ input: { "aria-label": `Choose ${f.original_name}` } }}
        />
      </TableCell>
      <TableCell>
        {f.original_name}
        <Typography variant="caption" sx={{ display: "block" }}>
          {f.team_name}
          {f.source ? ` · ${f.source}` : ""}
        </Typography>
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap", fontFamily: "monospace" }}>
        {utc(f.recorded).slice(0, 16)}
      </TableCell>
      <TableCell>{f.cases.map((c) => c.name).join(", ") || "—"}</TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        {f.due ? day(f.due) : "Kept"}
        {f.on_hold && (
          <Chip
            size="small"
            color="warning"
            label="Legal hold"
            sx={{ ml: 1 }}
          />
        )}
        {f.disposal_id && (
          <Chip
            size="small"
            color="info"
            label="Disposal requested"
            sx={{ ml: 1 }}
          />
        )}
      </TableCell>
      <TableCell>{f.rule}</TableCell>
    </TableRow>
  );
}

function names(files: RetentionFile[]) {
  const shown = files.slice(0, 5).map((f) => f.original_name);
  return files.length > 5
    ? `${shown.join(", ")} and ${files.length - 5} more`
    : shown.join(", ");
}

function RequestDisposal({
  files,
  onClose,
  onDone,
}: {
  files: RetentionFile[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState("");
  const [now] = useState(() => Date.now());
  const early = files.some((f) => !f.due || Date.parse(f.due) > now);
  const request = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/disposals", {
          body: { file_ids: files.map((f) => f.id), reason },
        }),
      ),
    onSuccess: onDone,
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Request disposal</DialogTitle>
      <DialogContent>
        <Typography gutterBottom>{names(files)}</Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Once someone else approves it, the recordings and their working copies
          are deleted for good. Their records stay, so the audit trail is
          complete, and a signed destruction certificate is made.
        </Typography>
        {early && (
          <Alert severity="warning" sx={{ my: 1 }}>
            Some of these aren&apos;t due yet. The request records that.
          </Alert>
        )}
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ mt: 1 }}
        />
        {request.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {request.error.message}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          disabled={!reason.trim() || request.isPending}
          onClick={() => request.mutate()}
        >
          Request disposal
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function KeepLonger({
  files,
  onClose,
  onDone,
}: {
  files: RetentionFile[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [until, setUntil] = useState("");
  const [reason, setReason] = useState("");
  const keep = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/retention/extend", {
          body: {
            file_ids: files.map((f) => f.id),
            keep_until: `${until}T00:00:00Z`,
            reason,
          },
        }),
      ),
    onSuccess: onDone,
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Keep longer</DialogTitle>
      <DialogContent>
        <Typography gutterBottom>{names(files)}</Typography>
        <TextField
          type="date"
          label="Keep until (UTC)"
          value={until}
          onChange={(e) => setUntil(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ mt: 1 }}
        />
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ mt: 2 }}
        />
        {keep.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {keep.error.message}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!until || !reason.trim() || keep.isPending}
          onClick={() => keep.mutate()}
        >
          Keep
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function PlaceHold({
  kind,
  id,
  name,
  onClose,
  onDone,
}: {
  kind: "case" | "file";
  id: string;
  name: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState("");
  const place = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/holds", {
          body: { target_kind: kind, target_id: id, reason },
        }),
      ),
    onSuccess: onDone,
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Legal hold on {name}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {kind === "case"
            ? "Every recording in the case is kept, whatever its dates, until the hold is lifted."
            : "The recording is kept, whatever its dates, until the hold is lifted."}
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Reason, e.g. the proceedings it is for"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ mt: 1 }}
        />
        {place.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {place.error.message}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!reason.trim() || place.isPending}
          onClick={() => place.mutate()}
        >
          Place hold
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Holds() {
  const qc = useQueryClient();
  const holds = useQuery({
    queryKey: ["holds"],
    queryFn: () => unwrap(api.GET("/api/holds")),
  });
  const [lifting, setLifting] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const lift = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.POST("/api/holds/{id}/lift", {
          params: { path: { id } },
          body: { reason },
        }),
      ),
    onSuccess: () => {
      setLifting(null);
      setReason("");
      void qc.invalidateQueries({ queryKey: ["holds"] });
      void qc.invalidateQueries({ queryKey: ["retention"] });
    },
  });
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      component="section"
      aria-label="Legal holds"
    >
      <Typography variant="h6" component="h3" gutterBottom>
        Legal holds
      </Typography>
      {holds.data?.length === 0 ? (
        <Typography color="text.secondary">No holds in force.</Typography>
      ) : (
        <TableContainer>
          <Table size="small" aria-label="Holds in force">
            <TableHead>
              <TableRow>
                <TableCell>On</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Placed</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {holds.data?.map((h) => (
                <TableRow key={h.id}>
                  <TableCell>
                    {h.target_kind === "case" ? "Case" : "Recording"}{" "}
                    {h.target_name}
                  </TableCell>
                  <TableCell>{h.reason}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {h.placed_by}
                    <Typography variant="caption" sx={{ display: "block" }}>
                      {day(h.placed_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {h.can_lift && (
                      <Button size="small" onClick={() => setLifting(h.id)}>
                        Lift
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {lifting && (
        <Dialog open onClose={() => setLifting(null)} fullWidth maxWidth="sm">
          <DialogTitle>Lift legal hold</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              sx={{ mt: 1 }}
            />
            {lift.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {lift.error.message}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLifting(null)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!reason.trim() || lift.isPending}
              onClick={() => lift.mutate(lifting)}
            >
              Lift hold
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Paper>
  );
}

function Disposals() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["disposals"],
    queryFn: () => unwrap(api.GET("/api/disposals")),
    refetchInterval: (q) =>
      q.state.data?.some((d) => d.state === "disposing") ? 2000 : false,
  });
  const [deciding, setDeciding] = useState<{
    d: DisposalSummary;
    approve: boolean;
  } | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["disposals"] });
    void qc.invalidateQueries({ queryKey: ["retention"] });
  };
  const retry = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.POST("/api/disposals/{id}/retry", { params: { path: { id } } }),
      ),
    onSuccess: refresh,
  });
  if (!list.data?.length) return null;
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      component="section"
      aria-label="Disposals"
    >
      <Typography variant="h6" component="h3" gutterBottom>
        Disposals
      </Typography>
      {retry.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {retry.error.message}
        </Alert>
      )}
      <TableContainer>
        <Table size="small" aria-label="Disposal requests">
          <TableHead>
            <TableRow>
              <TableCell>Requested (UTC)</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Decided by</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {list.data.map((d) => {
              const s = STATE[d.state] ?? {
                label: d.state,
                color: "default" as const,
              };
              return (
                <TableRow key={d.id}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {d.requested_by}
                    <Typography
                      variant="caption"
                      sx={{ display: "block", fontFamily: "monospace" }}
                    >
                      {utc(d.requested_at).slice(0, 16)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {d.reason}
                    <Typography variant="caption" sx={{ display: "block" }}>
                      {d.file_count} recording{d.file_count === 1 ? "" : "s"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={s.label} color={s.color} />
                    {d.error && (
                      <Typography variant="caption" sx={{ display: "block" }}>
                        {d.error}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {d.decided_by ?? "—"}
                    {d.self_approved && " (self-approval)"}
                    {d.decision_note && (
                      <Typography variant="caption" sx={{ display: "block" }}>
                        {d.decision_note}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {d.can_decide && (
                      <>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => setDeciding({ d, approve: true })}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          onClick={() => setDeciding({ d, approve: false })}
                        >
                          Reject
                        </Button>
                      </>
                    )}
                    {d.state === "failed" && (
                      <Button
                        size="small"
                        disabled={retry.isPending}
                        onClick={() => retry.mutate(d.id)}
                      >
                        Retry
                      </Button>
                    )}
                    {d.state === "done" && (
                      <Button
                        size="small"
                        variant="contained"
                        href={`/api/disposals/${d.id}/certificate`}
                      >
                        Certificate
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {deciding && (
        <Decide
          {...deciding}
          onClose={() => setDeciding(null)}
          onDone={() => {
            setDeciding(null);
            refresh();
          }}
        />
      )}
    </Paper>
  );
}

function Decide({
  d,
  approve,
  onClose,
  onDone,
}: {
  d: DisposalSummary;
  approve: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [note, setNote] = useState("");
  const [password, setPassword] = useState("");
  const detail = useQuery({
    queryKey: ["disposals", d.id],
    queryFn: () =>
      unwrap(
        api.GET("/api/disposals/{id}", { params: { path: { id: d.id } } }),
      ),
  });
  const decide = useMutation({
    mutationFn: () => {
      const body = { note, password: d.needs_password ? password : null };
      const params = { path: { id: d.id } };
      return unwrap(
        approve
          ? api.POST("/api/disposals/{id}/approve", { params, body })
          : api.POST("/api/disposals/{id}/reject", { params, body }),
      );
    },
    onSuccess: onDone,
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {approve ? "Approve" : "Reject"} disposal requested by {d.requested_by}
      </DialogTitle>
      <DialogContent>
        <Typography gutterBottom>Reason: {d.reason}</Typography>
        {approve && (
          <Alert severity="warning" sx={{ my: 1 }}>
            Approving deletes these recordings and their working copies for
            good. Only their records and the destruction certificate remain.
          </Alert>
        )}
        <TableContainer sx={{ maxHeight: 260 }}>
          <Table size="small" aria-label="Recordings to dispose of">
            <TableHead>
              <TableRow>
                <TableCell>Recording</TableCell>
                <TableCell>Library</TableCell>
                <TableCell>Recorded (UTC)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detail.data?.files.map((f) => (
                <TableRow key={f.id}>
                  <TableCell>{f.original_name}</TableCell>
                  <TableCell>{f.library}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>
                    {f.start_utc ? utc(f.start_utc).slice(0, 16) : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Note (recorded in the audit log)"
          value={note}
          onChange={(ev) => setNote(ev.target.value)}
          sx={{ mt: 2 }}
        />
        {d.needs_password && (
          <>
            <Alert severity="info" sx={{ mt: 2 }}>
              This is your own request. Enter your password to{" "}
              {approve ? "approve" : "reject"} it; it is recorded as a
              self-approval in the audit log and on the certificate.
            </Alert>
            <TextField
              fullWidth
              type="password"
              autoComplete="current-password"
              label="Your password"
              value={password}
              onChange={(ev) => setPassword(ev.target.value)}
              sx={{ mt: 2 }}
            />
          </>
        )}
        {decide.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {decide.error.message}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color={approve ? "error" : "primary"}
          disabled={decide.isPending || (d.needs_password && !password)}
          onClick={() => decide.mutate()}
        >
          {approve ? "Approve and dispose of" : "Reject"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
